import { useEffect, useState } from 'react';
import { useT } from '@/i18n/useT';

/**
 * Destructive action with a confirm step, without a modal.
 *
 * First click arms it and states what will happen; second click does it. Cheaper
 * than a dialog, keeps the explanation next to the button, and it disarms itself
 * after a few seconds so a stray click cannot leave a live "delete" primed on
 * screen for the next person who walks past.
 */
export function ConfirmButton({
  label,
  confirmText,
  onConfirm,
  busy = false,
  className = 'bot-btn danger',
}: {
  label: string;
  confirmText: string;
  onConfirm: () => void;
  busy?: boolean;
  className?: string;
}) {
  const { t } = useT();
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 6000);
    return () => clearTimeout(id);
  }, [armed]);

  if (!armed) {
    return (
      <button type="button" className={className} disabled={busy} onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }

  return (
    <div className="confirm-row">
      <span className="confirm-text">{confirmText}</span>
      <div className="confirm-actions">
        <button type="button" className="bot-btn ghost" onClick={() => setArmed(false)}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="bot-btn danger"
          disabled={busy}
          onClick={() => {
            setArmed(false);
            onConfirm();
          }}
        >
          {t('common.confirm')}
        </button>
      </div>
    </div>
  );
}
